// Imported by dist path on purpose: plyr only declares an "exports" map and
// no "main", which this Parcel version ignores, so the bare specifier fails.
import Plyr from 'plyr/dist/plyr';
import { bootBlock } from '../../src/js/block-boot';

const BLOCK = '.featured-videos';
const TRIGGER = '.featured-videos__trigger';
const MODAL = '.featured-videos__modal';
const MOUNT = '.featured-videos__modal-player';
const CLOSE = '[data-video-close]';
const BODY_CLASS = 'featured-videos-modal-open';

// Listed in the order they are laid out, so tabbing follows what is on screen.
const CONTROLS = [
  'progress',
  'play',
  'mute',
  'volume',
  'current-time',
  'duration',
  'captions',
  'settings',
  'fullscreen',
];

// Only one modal can be open at a time, so a single reference is enough even
// with several blocks on the page.
let activePlayer = null;

function buildOptions() {
  const options = {
    controls: CONTROLS,
    settings: ['captions', 'quality', 'speed'],
    vimeo: { dnt: true },
  };

  // Falls back to the Plyr CDN sprite when the theme copy is unavailable.
  const { iconUrl } = window.featuredVideos || {};

  if (iconUrl) {
    options.iconUrl = iconUrl;
  }

  return options;
}

// Plyr needs the id and the privacy hash as separate attributes; handing it a
// full player URL here silently drops the hash and unlisted videos fail.
function createTarget({ videoId, videoHash }) {
  const target = document.createElement('div');

  target.dataset.plyrProvider = 'vimeo';
  target.dataset.plyrEmbedId = videoId;

  if (videoHash) {
    target.dataset.plyrEmbedHash = videoHash;
  }

  return target;
}

// The click that opened the modal may no longer count as a gesture by the time
// the embed is ready, so a blocked autoplay is expected, not an error.
function startPlayback(instance) {
  const started = instance.play();

  if (started && typeof started.catch === 'function') {
    started.catch(() => {});
  }
}

// Destroying rather than pausing: it takes the iframe out of the page, so a
// closed modal can never keep playing audio in the background. Safe to call
// more than once, since several close routes can land here for one dismissal.
function teardown(modal) {
  if (activePlayer) {
    activePlayer.destroy();
    activePlayer = null;
  }

  const mount = modal.querySelector(MOUNT);

  if (mount) {
    mount.replaceChildren();
  }

  modal.ownerDocument.body.classList.remove(BODY_CLASS);
}

function openModal(block, dataset) {
  const modal = block.querySelector(MODAL);
  if (!modal || typeof modal.showModal !== 'function') return;

  const mount = modal.querySelector(MOUNT);
  if (!mount) return;

  if (dataset.videoTitle) {
    modal.setAttribute('aria-label', dataset.videoTitle);
  }

  const target = createTarget(dataset);

  mount.replaceChildren(target);
  modal.showModal();
  modal.ownerDocument.body.classList.add(BODY_CLASS);

  const instance = new Plyr(target, buildOptions());

  activePlayer = instance;

  // Bound to this instance, not to whatever `activePlayer` happens to hold when
  // the embed finally loads: closing the modal before then would otherwise call
  // play() on null, and reopening a different video would start the wrong one.
  instance.once('ready', () => {
    if (activePlayer !== instance) return;

    startPlayback(instance);
  });
}

// Every dismissal goes through here rather than through the dialog's own
// 'close' event: that event is not observable in every engine, and leaving the
// iframe in place would keep the audio running.
function closeModal(modal) {
  teardown(modal);
  modal.close();
}

// Guards against ACF handing us the same preview element twice.
const booted = new WeakSet();

// `root` is the document on the front end and the block element in the editor,
// where Block API v3 renders previews inside the canvas iframe. Listeners are
// bound to it rather than to the global document so both cases work.
function initFeaturedVideos(root) {
  if (booted.has(root)) return;
  booted.add(root);

  root.addEventListener('click', (event) => {
    const closer = event.target.closest(CLOSE);

    if (closer) {
      closeModal(closer.closest(MODAL));
      return;
    }

    // A click landing on the dialog itself is a click on the backdrop area
    // around the content.
    if (event.target.matches(MODAL)) {
      closeModal(event.target);
      return;
    }

    const trigger = event.target.closest(TRIGGER);
    if (!trigger) return;

    const block = trigger.closest(BLOCK);
    const player = trigger.closest('.featured-videos__player');
    if (!block || !player || !player.dataset.videoId) return;

    openModal(block, player.dataset);
  });

  // The dialog closes itself on Escape, so this is only here to run the
  // teardown alongside it.
  root.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    const modal = root.querySelector(`${MODAL}[open]`);

    if (modal) {
      closeModal(modal);
    }
  });

  // Safety net for any route that closes the dialog without going through the
  // handlers above. 'close' does not bubble, hence the capture phase.
  root.addEventListener('close', (event) => {
    const modal = event.target;

    if (modal instanceof HTMLElement && modal.matches(MODAL)) {
      teardown(modal);
    }
  }, true);
}

bootBlock('featured-videos', initFeaturedVideos);
